import { Box, Typography, useTheme } from "@mui/material";
import NoTaskOpenIcon from "../../icons/NoTaskOpenIcon";

/**
 * NoDataPresentUI
 *
 * A reusable UI component that displays a message when no data or tasks are available.
 * This component includes an icon, a heading, and customizable styles, making it suitable for
 * displaying a friendly "no data" message in various sections of an app.
 *
 * @param {string} heading - Text to display as the main message. Defaults to 'Hey! No task has open yet!'.
 * @param {JSX.Element} icon - An icon component to display above the heading. Defaults to `NoTaskOpenIcon`.
 * @param {object} containerSx - Custom styles for the container box, allowing for style overrides.
 * @param {...object} props - Any additional props to pass to the container box.
 *
 * @returns {JSX.Element} - A centered message box with an icon and heading, indicating no data presence.
 */
const NoDataPresentUI = ({
    heading = "Hey! No task has open yet!",
    icon = <NoTaskOpenIcon />,
    containerSx = {},
    ...props
}) => {
    const theme = useTheme();
    return (
        <Box
            width={"100%"}
            height={"100%"}
            display={"flex"}
            justifyContent={"center"}
            alignItems={"center"}
            flexDirection={"column"}
            gap={"1.5rem"}
            padding={"1rem"}
            sx={{
                ...containerSx,
            }}
            {...props}>
            {icon}
            <Typography
                component={"p"}
                color={"grey.600"}
                textAlign={"center"}
                fontSize={"customFontSize.large"}
                fontWeight={"fontWeightSemiBold"}
                lineHeight={"normal"}>
                {heading}
            </Typography>
        </Box>
    );
};

export default NoDataPresentUI;
